import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { listConsentTemplates, sendConsent } from "../api/consent";

/**
 * FastAPI's `{detail}` if there is one, the raw message otherwise — the same
 * job WarmTransferPanel's `friendlyError` does, kept local for the same
 * reason. "Not permitted" tells the operator what to fix; "Request failed
 * with status code 403" does not.
 */
function errorText(err: unknown): string {
  const ax = err as { response?: { data?: { detail?: string } }; message?: string };
  return ax?.response?.data?.detail || ax?.message || "please try again";
}

/**
 * Mid-call consent-SMS control for the agent console. Lists the account's
 * active consent templates; tapping one texts the caller the agent is on with.
 * For a true agent login the recipient is resolved server-side from the live
 * call (the `to` sent here is ignored), so an agent can only ever text the
 * current caller. For an operator on the shared login — who has no seat in
 * their token — the backend trusts `to`, which is the same current-call number
 * the server resolved into the screen-pop (`phone`).
 */
export function ConsentPanel({ spoolId, phone }: { spoolId: number | null; phone: string | null }) {
  const [result, setResult] = useState<{ ok: boolean; text: string } | null>(null);
  const templates = useQuery({
    queryKey: ["consent-templates-agent"],
    queryFn: () => listConsentTemplates(false),
  });
  const send = useMutation({
    // `phone` is the number the server already resolved for the live call
    // (the screen-pop). An agent login's send ignores `to` and re-resolves
    // server-side; an OPERATOR on the shared login has no seat in their token,
    // so the backend's operator branch reads `to` — without it that send 422s
    // 'a valid US phone number is required' (beads u0np). spool_id stays as
    // log context.
    mutationFn: (templateId: number) =>
      sendConsent({ template_id: templateId, to: phone ?? undefined, spool_id: spoolId ?? undefined }),
    onSuccess: (r) => setResult({ ok: true, text: `Consent request sent to ${r.to}.` }),
    onError: (e) => setResult({ ok: false, text: errorText(e) }),
  });

  const rows = templates.data ?? [];

  // An account with no templates and a request that never came back are two
  // different problems with two different owners — "go write one" vs "your
  // seat can't read them". Collapsing both into the empty state sent the
  // operator off to re-create templates that already existed.
  const loadError = templates.isError ? errorText(templates.error) : null;

  return (
    <div className="mt-4 rounded-xl bg-white/5 p-4 ring-1 ring-white/10">
      <p className="mb-2 text-sm text-white/80">
        Send a consent request to <span className="font-mono">{phone ?? "this caller"}</span>. They reply YES to confirm.
      </p>
      {templates.isLoading ? (
        <p className="text-xs text-white/50">Loading templates…</p>
      ) : loadError ? (
        <p className="text-sm text-red-300" role="alert">
          Couldn't load your consent templates — {loadError}
        </p>
      ) : rows.length === 0 ? (
        <p className="text-xs text-white/50">No consent templates set up for your account yet.</p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {rows.map((t) => (
            <button
              key={t.consent_template_id}
              disabled={send.isPending}
              onClick={() => { setResult(null); send.mutate(t.consent_template_id); }}
              title={t.body}
              // Deliberately NOT the neutral white/10 pill the warm-transfer
              // destinations use. These two rows of buttons were previously
              // indistinguishable, and one of them dials a third party while
              // this one sends a text — an agent reaching for the wrong row
              // put a live call into a consult leg. They no longer share a
              // screen (AgentConsole opens one panel at a time), and they no
              // longer look alike either.
              className="rounded-lg bg-emerald-500/20 px-3 py-2 text-sm text-emerald-100 ring-1 ring-emerald-400/40 transition hover:bg-emerald-500/30 disabled:opacity-50"
            >
              {send.isPending ? "Sending…" : t.name}
            </button>
          ))}
        </div>
      )}
      {result && (
        <p className={`mt-3 text-sm ${result.ok ? "text-emerald-300" : "text-red-300"}`}>{result.text}</p>
      )}
    </div>
  );
}
